import { msg } from "@lingui/core/macro";
import { z } from "zod";
import type { IScheduledShift } from "./shifts";
import {
  activityOccurrenceStatusDictionary,
  groupedHomeVisitActivityOccurrenceSchema,
  groupOfHomeVisitActivityOccurrenceSchema,
  visitStatusSchema,
  type IGroupStatus,
} from "./activities";
import { timestampSchema } from "./date-and-time";
import { isEqual } from "date-fns";
import type { MessageDescriptor } from "@lingui/core";

const sortOccurrencesByStartTime = (occurrences: Array<{ start: Date }>) => {
  return [...occurrences].sort((a, b) => {
    if (a.start < b.start) {
      return -1;
    }
    if (a.start > b.start) {
      return 1;
    }
    return 0;
  });
};

const sortOccurrencesByEndTime = (occurrences: Array<{ end: Date }>) => {
  return [...occurrences].sort((a, b) => {
    if (a.end < b.end) {
      return -1;
    }
    if (a.end > b.end) {
      return 1;
    }
    return 0;
  });
};

const occurrencesAreOverlapping = (
  occurrences: Array<
    Pick<z.infer<typeof visitSchema>["occurrences"][0], "start" | "end">
  >,
) => {
  const latestStart =
    sortOccurrencesByStartTime(occurrences)[occurrences.length - 1]?.start;
  const earliestEnd = sortOccurrencesByEndTime(occurrences)[0]?.end;

  if (!latestStart || !earliestEnd) {
    return false;
  }

  if (earliestEnd > latestStart) {
    return true;
  }

  return false;
};

export const calculateVisitTimes = (visit: {
  occurrences: Array<
    Pick<
      z.infer<typeof visitSchema>["occurrences"][0],
      "start" | "end" | "timeOfDay"
    >
  >;
}) => {
  const { occurrences } = visit;
  const visitOccurrencesExcludingJit = occurrences.filter((occurrence) => {
    const isJit = isEqual(occurrence.start, occurrence.end);
    return !isJit;
  });

  if (visitOccurrencesExcludingJit.length === 0) {
    return {
      start: undefined,
      end: undefined,
    };
  }

  if (!occurrencesAreOverlapping(visitOccurrencesExcludingJit)) {
    return {
      start: undefined,
      end: undefined,
    };
  }

  const isAnyTimeOfDay = visitOccurrencesExcludingJit.every(
    (occurrence) => occurrence.timeOfDay === "Any",
  );

  const start = sortOccurrencesByStartTime(visitOccurrencesExcludingJit).at(
    -1,
  )?.start;
  const end = sortOccurrencesByEndTime(visitOccurrencesExcludingJit)[0]?.end;

  if (!start || !end) {
    return {
      start: undefined,
      end: undefined,
    };
  }

  return {
    start,
    end,
    isAnyTimeOfDay,
  };
};

export const isVisitOutsideShiftSpan = (
  visit: Parameters<typeof calculateVisitTimes>[0],
  shift: Pick<IScheduledShift, "startDateTime" | "endDateTime">,
) => {
  const { start, end } = calculateVisitTimes(visit);
  if (!start || !end) {
    return false;
  }
  return end <= shift.startDateTime || start >= shift.endDateTime;
};

export const getVisitStatusTimestamp = (
  visit: Pick<
    IVisit,
    "status" | "startedTravellingToAt" | "startedAt" | "finishedAt"
  >,
) => {
  const { status, startedTravellingToAt, startedAt, finishedAt } = visit;

  if (status === "travellingTo" && startedTravellingToAt) {
    return startedTravellingToAt;
  }

  if (status === "ongoing" && startedAt) {
    return startedAt;
  }

  if (status === "finished" && finishedAt) {
    return finishedAt;
  }

  return undefined;
};

export type IVisitStatus = z.infer<typeof visitStatusSchema>;

export const visitStatusDictionary: Record<IVisitStatus, MessageDescriptor> = {
  planned: msg`Planerat`,
  travellingTo: msg`På väg`,
  ongoing: activityOccurrenceStatusDictionary.ongoing,
  finished: activityOccurrenceStatusDictionary.finished,
};

// A group reads as its visit for as long as it has one, and says "Avbokat" when
// it no longer will. The label lives with the visit labels it extends, rather
// than with the group, so the two stay in step.
export const groupStatusDictionary: Record<IGroupStatus, MessageDescriptor> = {
  ...visitStatusDictionary,
  cancelled: msg`Avbokat`,
};

// A visit is a group of home visits once a route has picked it up — the same
// stop, reported by the route that is driving to it. So it is derived from the
// group rather than declared beside it, and the fields the two share cannot
// drift apart. Picked rather than omitted, so a new field on the group has to
// be offered to the visit deliberately instead of appearing on it.
//
// Three things differ, and each is a consequence of being routed:
//   - a routed group cannot be cancelled, so a visit carries the narrower
//     visitStatus, and always carries one;
//   - the route reports when the stop finished, which the group does not;
//   - the route does not send the group's planning fields — when it may happen,
//     what competences it needs, whether it takes two employees — nor the
//     category, which is HomeVisit for every visit there is.
export const visitSchema = groupOfHomeVisitActivityOccurrenceSchema
  .pick({
    id: true,
    patient: true,
    assignees: true,
    startedTravellingToAt: true,
    startedAt: true,
  })
  .extend({
    occurrences: z.array(groupedHomeVisitActivityOccurrenceSchema),
    status: visitStatusSchema,
    finishedAt: timestampSchema,
  });
export type IVisit = z.infer<typeof visitSchema>;
